// Справка „Изписвания": KPI (само активните), сегменти Всички / Изписване /
// Бракуване с фасетни броячи (пряк път към филтъра „Вид"), търсачка, филтри
// В ЗАГЛАВИЯТА НА КОЛОНИТЕ (Вид · Причина · Статус · Склад · Въвел · Бруто
// от–до) с ред „Филтри: …" над таблицата, сортиране по заглавията и таблицата.
// Горе — общата лента (период · Excel · Опресни; Excel-ът — `writeoffsExcel`).
// Клик на ред отваря документа; „Печат" и менюто „⋯" (Преглед · Редактирай ·
// Анулирай) са на реда. Периодът по подразбиране е „Този месец" — днешните са
// на началния екран. Всички складове; анулираните стоят зачертани (BE с
// редизайна, `status=all`).
import { useMemo, useState, type FC } from "react";
import { AppstoreOutlined, EuroOutlined, ExportOutlined, FileSearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { useWriteoffsReport } from "../../../../hooks/useInventoryWriteoffs";
import type { Writeoff } from "../../../../api/inventory";
import { exportToExcel } from "../../../../utils/excelExport";
import { InventoryTheme } from "../../components/InventoryTheme";
import { PageHeader } from "../../components/list/PageHeader";
import {
  EmptyScreen,
  KpiGrid,
  SearchBox,
  SectionCard,
  SegmentedCounts,
  type KpiCard,
} from "../../components/list/ListBits";
import type { RowAction } from "../../components/list/RowActionsMenu";
import { ReportToolbar } from "../../components/list/ReportToolbar";
import { refreshOf } from "../../components/list/reportRefresh";
import { useShowMore } from "../../components/list/useShowMore";
import { periodOfPreset, periodToParams, type Period } from "../../components/period/periodPresets";
import { FilterSummaryLine } from "../../components/filters/FilterSummaryLine";
import { allSections, useColumnFilters, type ColumnSections } from "../../components/filters/useColumnFilters";
import { formatMoney } from "../../components/numbers";
import { useWriteoffDocument } from "../../writeoff/components/useWriteoffDocument";
import {
  isWriteoffAnnulled,
  reasonLabelOf,
  reasonSpellingsOf,
  sumActiveWriteoffs,
} from "../../writeoff/components/writeoffMeta";
import { ReportLoading, ReportNoMatch, ShowMoreRow } from "../components/ReportListBits";
import {
  EMPTY_WRITEOFF_FILTERS,
  kindSegmentOf,
  searchWriteoffs,
  sortWriteoffs,
  writeoffFilterSpecs,
  type WriteoffFilterKey,
  type WriteoffKindKey,
  type WriteoffSort,
  type WriteoffSortKey,
} from "./writeoffsReportModel";
import { WriteoffsReportTable, type WriteoffsDensity } from "./WriteoffsReportTable";
import { writeoffExcelColumns, writeoffExcelRows } from "./writeoffsExcel";

// Layout-ът (Inventory.tsx) подава общите props на всяка страница; справката
// е за всички складове и не ги ползва.
type LayoutProps = {
  onOpenMobileMenu?: () => void;
  isNarrow?: boolean;
  warehouseId?: number | null;
};

export const WriteoffsReport: FC<LayoutProps> = () => {
  const { t } = useTranslation();
  const { isNarrow, isWide } = useViewport();
  const writeoffDocument = useWriteoffDocument();

  const [period, setPeriod] = useState<Period>(() => periodOfPreset("thisMonth"));
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<WriteoffSort>({ key: "date", dir: "desc" });
  // Без склад → изписванията от всички складове (както заварения списък)
  const writeoffsQuery = useWriteoffsReport({
    ...periodToParams(period),
    status: "all",
  });
  const { data: writeoffs = [], isLoading } = writeoffsQuery;
  const { refresh, refreshing } = refreshOf(writeoffsQuery);

  // ─── Филтри в колоните (броячите — фасетни, по периода след търсенето) ──
  const reasonSpellings = useMemo(() => reasonSpellingsOf(writeoffs), [writeoffs]);
  const specs = useMemo(
    () =>
      writeoffFilterSpecs({
        titles: {
          kinds: t("inventoryPage.cfKind"),
          reasons: t("inventoryPage.colReason"),
          statuses: t("inventoryPage.colStatus"),
          warehouses: t("inventoryPage.colWarehouse"),
          users: t("inventoryPage.filterEnteredBy"),
          gross: t("inventoryPage.colGross"),
        },
        kindLabel: (value) => (value === "scrap" ? t("inventoryPage.scrap") : t("inventoryPage.writeoff")),
        // стойността е ключът — етикетът е последното изписване на причината
        reasonLabel: (key) =>
          key === "—" ? t("inventoryPage.woNoReason") : reasonLabelOf(reasonSpellings.get(key) ?? key, t),
        statusLabel: (value) =>
          value === "annulled" ? t("inventoryPage.segAnnulled") : t("inventoryPage.woStatusActive"),
        formatMoney,
      }),
    [t, reasonSpellings],
  );
  const reasonLabel = (reason: string) => reasonLabelOf(reason, t);
  const searched = useMemo(
    () => searchWriteoffs(writeoffs, query, (reason) => reasonLabelOf(reason, t)),
    [writeoffs, query, t],
  );
  const filters = useColumnFilters({ specs, empty: EMPTY_WRITEOFF_FILTERS, universe: writeoffs, base: searched });
  const shown = useMemo(() => sortWriteoffs(filters.rows, sort), [filters.rows, sort]);
  const { visible, hiddenCount, showMore } = useShowMore(shown, [period, filters.state, query, sort]);
  const periodTotals = sumActiveWriteoffs(writeoffs);
  const shownTotals = sumActiveWriteoffs(shown);
  const clearAll = () => {
    filters.clearAll();
    setQuery("");
  };

  // Статусът — само когато в периода има анулирани (или вече е избран)
  const hasAnnulled = writeoffs.some(isWriteoffAnnulled);
  const sections: ColumnSections<WriteoffFilterKey> = {
    kinds: filters.section("kinds"),
    reasons: filters.section("reasons"),
    statuses: hasAnnulled || filters.state.statuses.length ? filters.section("statuses") : undefined,
    warehouses: filters.section("warehouses"),
    users: filters.section("users"),
    gross: filters.section("gross"),
  };

  // Сегментите = филтърът „Вид" (броячите — фасетни, както в попъпа)
  const kindCounts = new Map(filters.facet("kinds").map((option) => [option.value, option.count]));
  const onKindSegment = (key: WriteoffKindKey) =>
    filters.setValue("kinds", key === "all" ? [] : [key]);

  const kpiCards: KpiCard[] = [
    { label: t("inventoryPage.kpiWriteoffs"), value: String(periodTotals.count), Icon: ExportOutlined },
    {
      label: t("inventoryPage.kpiValueGross"),
      value: formatMoney(periodTotals.gross),
      Icon: EuroOutlined,
      tone: "brand",
    },
    { label: t("inventoryPage.kpiProducts"), value: String(periodTotals.products), Icon: AppstoreOutlined },
  ];

  const onSort = (key: WriteoffSortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "warehouse" ? "asc" : "desc" },
    );

  // Менюто „⋯": Принтирай · Преглед · [Редактирай · Анулирай] (само активните)
  const actionsOf = (writeoff: Writeoff): RowAction[] => {
    const base: RowAction[] = [
      {
        key: "print",
        label: t("inventoryPage.print"),
        onClick: () => writeoffDocument.open(writeoff, { autoPrint: true }),
      },
      {
        key: "view",
        label: t("inventoryPage.viewDocument"),
        onClick: () => writeoffDocument.open(writeoff),
      },
    ];
    if (isWriteoffAnnulled(writeoff)) return base;
    return [
      ...base,
      {
        key: "edit",
        label: t("inventoryPage.docEdit"),
        dividerBefore: true,
        onClick: () => writeoffDocument.edit(writeoff),
      },
      {
        key: "annul",
        label: t("inventoryPage.docDelete"),
        danger: true,
        onClick: () => writeoffDocument.annul(writeoff),
      },
    ];
  };

  // Каквото е на екрана (филтри, търсене, подредба) + редът „Общо"
  const exportXls = () =>
    exportToExcel({
      filename: "izpisvaniya",
      sheetName: t("inventoryPage.navWriteoffsReport"),
      rows: writeoffExcelRows(shown, {
        kindOf: (writeoff) => (writeoff.kind === "scrap" ? t("inventoryPage.scrap") : t("inventoryPage.writeoff")),
        reasonOf: (writeoff) => (writeoff.reason ? reasonLabel(writeoff.reason) : t("inventoryPage.woNoReason")),
        annulled: t("inventoryPage.statusAnnulled"),
        total: t("inventoryPage.periodTotalActive"),
      }),
      columns: writeoffExcelColumns(t),
    });

  const density: WriteoffsDensity = isNarrow ? "cards" : isWide ? "full" : "medium";

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          kicker={t("inventoryPage.kickerReport")}
          title={t("inventoryPage.navWriteoffsReport")}
          right={
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              filterSections={allSections(sections)}
              onExport={exportXls}
              exportDisabled={!shown.length}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <KpiGrid cards={kpiCards} />
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedCounts
              value={kindSegmentOf(filters.state.kinds)}
              onChange={onKindSegment}
              options={[
                { key: "all", label: t("inventoryPage.segAll"), count: filters.facetTotal("kinds") },
                { key: "writeoff", label: t("inventoryPage.writeoff"), count: kindCounts.get("writeoff") ?? 0 },
                { key: "scrap", label: t("inventoryPage.scrap"), count: kindCounts.get("scrap") ?? 0 },
              ]}
            />
            <SearchBox value={query} onChange={setQuery} placeholder={t("inventoryPage.woReportSearchPh")} />
          </div>
          <SectionCard>
            <FilterSummaryLine
              countLabel={t("inventoryPage.woDocsCount", { count: shown.length })}
              items={filters.summary}
              onClearAll={filters.clearAll}
            >
              <span>
                {t("inventoryPage.colGross")}{" "}
                <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(shownTotals.gross)}</span>
              </span>
            </FilterSummaryLine>
            {isLoading ? (
              <ReportLoading />
            ) : writeoffs.length === 0 ? (
              <EmptyScreen Icon={FileSearchOutlined} tone="brand" title={t("inventoryPage.woReportNone")} />
            ) : (
              <WriteoffsReportTable
                rows={visible}
                density={density}
                sort={sort}
                onSort={onSort}
                filters={sections}
                reasonLabel={reasonLabel}
                totalNet={shownTotals.net}
                totalGross={shownTotals.gross}
                onOpen={(writeoff) => writeoffDocument.open(writeoff)}
                onPrint={(writeoff) => writeoffDocument.open(writeoff, { autoPrint: true })}
                actionsOf={actionsOf}
                footer={
                  shown.length ? (
                    <ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />
                  ) : (
                    <ReportNoMatch onClear={clearAll} />
                  )
                }
              />
            )}
          </SectionCard>
        </div>
        {writeoffDocument.modals}
      </div>
    </InventoryTheme>
  );
};

export default WriteoffsReport;
